import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import { Link } from 'react-router-dom';
import 'highlight.js/styles/github.css';

const customComponents = {
    // Headings
    h1: ({ children, ...props }) => (
        <h1 className="text-3xl font-bold text-[#222425] mt-10 mb-6" {...props}>{children}</h1>
    ),
    h2: ({ children, ...props }) => (
        <h2 className="text-2xl font-bold text-[#222425] mt-8 mb-4 border-b border-gray-100 pb-2" {...props}>
            {children}
        </h2>
    ),
    h3: ({ children, ...props }) => (
        <h3 className="text-xl font-bold text-[#222425] mt-6 mb-3" {...props}>{children}</h3>
    ),
    // Paragraphs
    p: ({ children }) => (
        <p className="text-gray-700 leading-relaxed mb-6">{children}</p>
    ),
    // Blockquote
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-[#00DA76] pl-5 italic text-gray-600 my-8 text-lg leading-relaxed bg-gray-50 py-4 pr-4 rounded-r-lg">
            {children}
        </blockquote>
    ),
    // Tables
    table: ({ children }) => (
        <div className="overflow-x-auto my-8 rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm divide-y divide-gray-200">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }) => (
        <th className="px-6 py-4 text-left font-semibold text-gray-700 uppercase tracking-wider">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="px-6 py-4 border-t border-gray-100 text-gray-600">{children}</td>
    ),
    // Lists
    ul: ({ children }) => <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">{children}</ol>,
    li: ({ children }) => <li>{children}</li>,
    // Code
    code: ({ inline, className, children, ...props }) => {
        if (inline) {
            return (
                <code className="bg-gray-100 text-[#222425] px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                    {children}
                </code>
            );
        }
        return (
            <div className="my-6 rounded-xl overflow-hidden shadow-sm">
                <code className={`${className} block p-4 bg-gray-900 text-gray-100 text-sm overflow-x-auto`} {...props}>
                    {children}
                </code>
            </div>
        );
    },
    // Ticker Chips Detection
    strong: ({ children }) => {
        const text = String(children);
        // Detect $TICKER pattern
        if (/^\$[A-Z]+$/.test(text)) {
            return (
                <span className="text-[#00DA76] font-bold">
                    {text}
                </span>
            );
        }
        return <strong className="font-bold text-[#222425]">{children}</strong>;
    },
    // Links
    a: ({ href, children }) => (
        <a href={href} className="text-[#00DA76] hover:underline font-medium">{children}</a>
    ),
    // Images
    img: ({ src, alt }) => (
        <figure className="my-10">
            <img src={src} alt={alt} className="rounded-2xl w-full shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300" />
            {alt && <figcaption className="text-center text-sm text-gray-500 mt-4 font-medium italic">{alt}</figcaption>}
        </figure>
    ),
    hr: () => <hr className="my-12 border-gray-100" />
};

const ArticleBody = ({ markdown }) => {
    return (
        <div className="prose prose-lg max-w-none">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                    rehypeHighlight,
                    rehypeSlug,
                    [rehypeAutolinkHeadings, { behavior: 'wrap' }]
                ]}
                components={customComponents}
            >
                {markdown}
            </ReactMarkdown>
        </div>
    );
};

export default ArticleBody;
